// ============================================
// FILE: /frontend/src/components/dashboard/SubscriptionStatus.jsx
// PURPOSE: Subscription tier and usage display
// UPDATED: Added new user detection + conditional routing
// ============================================

import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Crown, TrendingUp, Calendar } from 'lucide-react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';

const TIER_CONFIG = {
  BRONZE: { icon: '🥉', color: 'text-orange-600', bg: 'bg-orange-50' },
  SILVER: { icon: '🥈', color: 'text-gray-600', bg: 'bg-gray-50' },
  GOLD: { icon: '🥇', color: 'text-yellow-600', bg: 'bg-yellow-50' },
  PLATINUM: { icon: '💎', color: 'text-purple-600', bg: 'bg-purple-50' }
};

export default function SubscriptionStatus({ subscription }) {
  console.log('🎨 [SubscriptionStatus] Rendering with subscription:', subscription);

  const config = TIER_CONFIG[subscription.tier] || TIER_CONFIG.BRONZE;
  const percentUsed = (subscription.adsUsed / subscription.adsLimit) * 100;
  const isExpired = subscription.daysRemaining === 0;
  
  // Detect if user is NEW (Bronze + Expired + 0 ads used)
  const isNewUser = subscription.tier === 'BRONZE' && isExpired && subscription.adsUsed === 0;
  
  // Determine button text
  const getButtonText = () => {
    if (isNewUser) return 'Subscribe';
    if (isExpired) return 'Renew Subscription';
    return 'Upgrade';
  };
  
  // Determine button link
  const getButtonLink = () => {
    if (isNewUser || isExpired) return '/listings/checkout';
    return '/subscription/upgrade';
  };
  
  // Determine status label
  const getStatusLabel = () => {
    if (isNewUser) return 'Subscribe';
    if (isExpired) return 'EXPIRED';
    return subscription.daysRemaining;
  };
  
  // Determine status label name
  const getStatusLabelName = () => {
    if (isNewUser) return 'Subscription Status';
    if (isExpired) return 'Subscription Status';
    return 'Days Remaining';
  };

  return (
    <Card className={`${config.bg} w-full overflow-hidden`}>
      <CardHeader className="p-3 sm:p-4 md:p-6">
        <CardTitle className="text-sm sm:text-base md:text-lg">Subscription Status</CardTitle>
      </CardHeader>
      <CardContent className="p-3 sm:p-4 md:p-6 pt-0">
        <div className="text-center mb-4 sm:mb-6">
          <div className="text-2xl sm:text-3xl md:text-4xl mb-1 sm:mb-2">{config.icon}</div>
          <h3 className={`text-xs sm:text-sm md:text-base font-bold ${config.color}`}>
            {subscription.tier} MEMBERSHIP
          </h3>
        </div>

        <div className="space-y-3 sm:space-y-4">
          {/* Ads Used Progress */}
          <div>
            <div className="flex justify-between text-xs sm:text-sm mb-1 sm:mb-2">
              <span>Ads Used</span>
              <span className="font-semibold">
                {subscription.adsUsed}/{subscription.adsLimit}
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2 sm:h-3">
              <div
                className={`h-2 sm:h-3 rounded-full transition-all ${
                  percentUsed >= 90 ? 'bg-red-500' :
                  percentUsed >= 70 ? 'bg-yellow-500' : 'bg-green-500'
                }`}
                style={{ width: `${Math.min(percentUsed, 100)}%` }}
              />
            </div>
          </div>

          {/* Status Display */}
          <div className="flex items-center justify-between p-2 sm:p-3 bg-white rounded-md">
            <div className="flex items-center gap-1 sm:gap-2">
              <Calendar className="w-3 h-3 sm:w-4 sm:h-4 text-muted-foreground flex-shrink-0" />
              <span className="text-xs sm:text-sm">
                {getStatusLabelName()}
              </span>
            </div>
            <span className={`font-bold text-sm sm:text-base md:text-lg ${(isExpired && !isNewUser) ? 'text-red-600' : isNewUser ? 'text-blue-600' : ''}`}>
              {getStatusLabel()}
            </span>
          </div>

          {/* Expiry Date */}
          <p className="text-[10px] sm:text-xs text-muted-foreground text-center">
            Expires: {new Date(subscription.expiresAt).toLocaleDateString()}
          </p>

          {/* Action Buttons - Stacked hamburger menu */}
          <div className="flex flex-col gap-2 sm:gap-3 mt-3 sm:mt-4">

          <Link 
            to={getButtonLink()}
            onClick={() => {
              if (isExpired) {
                sessionStorage.setItem('renewingSubscription', 'true');
              }
            }}
            className="bg-primary hover:bg-primary/90 text-white p-3 sm:p-4 rounded-lg transition-all hover:shadow-lg hover:scale-[1.02] active:scale-[0.98] flex items-center gap-2 sm:gap-3"
          >
            <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
            <span className="text-xs sm:text-sm font-medium truncate">
              {getButtonText()}
            </span>
          </Link>

            <Link 
              to="/dashboard/subscription-history"
              className="bg-white hover:bg-muted border text-foreground p-3 sm:p-4 rounded-lg transition-all hover:shadow-lg hover:scale-[1.02] active:scale-[0.98] flex items-center gap-2 sm:gap-3"
            >
              <Calendar className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
              <span className="text-xs sm:text-sm font-medium truncate">View History</span>
            </Link>
         </div>
        </div>
      </CardContent>

    </Card>
  );
}